import { SafeResourceUrl } from '@angular/platform-browser';
import { Timestamp } from '@angular/fire/firestore';

//#region INTERFACE IUserAccessToken

/**
 * ### IUserAccessToken
 * Interface representing a user access token.
 * Contains the access token and its associated timestamp.
 *
 * @interface IUserAccessToken
 * @description This interface holds the access token string and the timestamp when it was issued.
 *              The timestamp can either be a `Timestamp` object from Firebase or a numeric Unix timestamp.
 *              This structure is essential for managing token validity and expiration.
 *
 * @example
 * const accessToken: IUserAccessToken = {
 *   timestamp: { seconds: 1634309062, nanoseconds: 123000000 },
 *   token: 'abc123xyz456'
 * };
 */
export interface IUserAccessToken {
  /**
   * The timestamp when the access token was generated or issued.
   * This can either be an object containing `seconds` and `nanoseconds` as properties
   * (represented by the `IUserAccessTokenTimestamp` type, which is a `Timestamp` object from Firebase)
   * or a numeric value (e.g., Unix timestamp in seconds).
   *
   * @type {IUserAccessTokenTimestamp | number}
   * @example { seconds: 1634309062, nanoseconds: 123000000 }
   */
  timestamp: IUserAccessTokenTimestamp | number;

  /**
   * The access token string used for authentication.
   * This token is typically sent with requests to authenticate the user.
   *
   * @type {string}
   * @example 'abc123xyz456'
   */
  token: string;
}

//#endregion

//#region INTERFACE IUserAccessTokenURL

/**
 * ### IUserAccessTokenURL
 * Interface representing a user access token with additional URL-related properties.
 * Extends the `IUserAccessToken` interface to include URL details and associated parameters.
 *
 * @template UserAccessTokenParams - The type for the parameters associated with the access token URL.
 * @interface IUserAccessTokenURL
 * @description This interface extends `IUserAccessToken` by adding URL-related properties.
 *              It provides a way to generate a complete URL with the access token, along with any associated parameters,
 *              and optionally includes a sanitized, safe resource URL for embedding.
 *
 * @example
 * const tokenUrl: IUserAccessTokenURL<any> = {
 *   timestamp: { seconds: 1634309062, nanoseconds: 123000000 },
 *   token: 'abc123xyz456',
 *   baseURL: 'https://example.com/api',
 *   encodedParams: 'id=123&status=active',
 *   fullUrl: 'https://example.com/api?id=123&status=active',
 *   params: { id: 123, status: 'active' }
 * };
 */
export interface IUserAccessTokenURL<UserAccessTokenParams> extends IUserAccessToken {
  /**
   * The base URL used for the token request.
   *
   * @type {string}
   * @example 'https://example.com/api'
   */
  baseURL: string;

  /**
   * The encoded parameters that are associated with the access token URL.
   * These parameters are typically used in the URL query.
   *
   * @type {string}
   * @example 'id=123&status=active'
   */
  encodedParams: string;

  /**
   * The full URL including the base URL and encoded parameters.
   *
   * @type {string}
   * @example 'https://example.com/api?id=123&status=active'
   */
  fullUrl: string;

  /**
   * The parameters associated with the access token, with the type defined by the generic parameter.
   *
   * @template UserAccessTokenParams - The type for the parameters associated with the access token URL.
   * @type {UserAccessTokenParams}
   * @example { id: 123, status: 'active' }
   */
  params: UserAccessTokenParams;

  /**
   * Optional property for a fully safe resource URL.
   * This is typically used when the URL is sanitized and trusted for safe embedding in the DOM.
   *
   * @type {SafeResourceUrl}
   * @optional
   * @example 'safe-url'
   */
  fullSafeResourceURL?: SafeResourceUrl;
}

//#endregion

//#region INTERFACE IUserAccessTokenTimestamp

/**
 * ### IUserAccessTokenTimestamp
 * Alias for the `Timestamp` type from Firebase Firestore.
 * Represents the timestamp of a user access token with `seconds` and `nanoseconds` precision.
 *
 * @typedef {Timestamp} IUserAccessTokenTimestamp
 * @description This type is an alias for the `Timestamp` object returned by Firebase.
 *              It contains `seconds` and `nanoseconds` properties to store the precise time of the token creation.
 *              It is used to handle token expiration and manage authentication sessions accurately.
 *
 * @example
 * const timestamp: IUserAccessTokenTimestamp = { seconds: 1634309062, nanoseconds: 123000000 };
 */
export type IUserAccessTokenTimestamp = Timestamp;

//#endregion
